import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import { invoiceSent, invoiceSnoozed } from "store/actions/invoiceActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { InvoiceFooterProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { PageFooter, useSnoozeAction } from "components/common";
import InvoiceMoreActions from "./actions/moreActions";
import { useNavigate, useLocation } from "react-router-dom";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const InvoiceFooter: React.FC<InvoiceFooterProps> = (props) => {
  const { invoice, sendType } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const invoiceService = useInvoiceService();

  const onInvoiceEdit = async () => {
    navigate(UrlHelper.formatPath(`/Invoice/${props.invoice.id}`), {
      state: { background: location },
    });
  };

  const onSnoozeSuccess = () => {
    props.markAsDoneHandler && props.markAsDoneHandler();
    dispatch(invoiceSnoozed(invoice.id));
  };

  const { snoozeActionDialog } = useSnoozeAction(
    props.invoice.id,
    WintSuperkollAPIModel.TodoType.Invoice,
    onSnoozeSuccess
  );

  const onSnoozeClicked = async () => {
    dispatch(displayDialog(snoozeActionDialog));
  };

  const onShowFinalApproval = async () => {
    props.onShowFinalApproval && props.onShowFinalApproval();
  };

  const onShowReview = async () => {
    props.onShowReview && props.onShowReview();
  };

  const onSendInvoiceSuccess = () => {
    props.markAsDoneHandler && props.markAsDoneHandler();
    dispatch(invoiceSent(invoice!.id));
  };

  const getActions = () => {
    let actions: ActionButton[] = [];
    if (props.invoice.status === WintSuperkollAPIModel.InvoiceStatus.NotSent) {
      actions = [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit),
          onClick: onInvoiceEdit,
          type: StyleEnums.DisplayType.Info,
        },
      ];
      if (props.isFinalPreview) {
        actions.push({
          text: invoiceService.getInvoiceSendMethodText(sendType),
          onClick: props.onSendInvoice,
          type: StyleEnums.DisplayType.Success,
          isDisabled: props.isValid === false,
          onActionSuccess: onSendInvoiceSuccess,
        });
      } else if (!props.isFinalPreview && props.isComplete !== false) {
        actions.push({
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FinalReview),
          onClick: onShowFinalApproval,
          type: StyleEnums.DisplayType.Success,
        });
      }
    } else if (props.invoice.status === WintSuperkollAPIModel.InvoiceStatus.OverdueReminderNotSent) {
      actions = [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Snooze),
          onClick: onSnoozeClicked,
          type: StyleEnums.DisplayType.Info,
        },
      ];
    }

    !props.hideSave &&
      actions.push({
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
        onClick: props.createOrUpdateInvoice,
        type: StyleEnums.DisplayType.Success,
        isDisabled: props.isValid === false,
        successMessage: TranslationKeys.Invoice.SaveSuccessMessage,
        onActionSuccess: props.reloadHandler,
      });

    return actions;
  };

  return (
    <PageFooter
      actions={getActions()}
      moreActions={
        !props.isFinalPreview ? (
          <InvoiceMoreActions
            invoice={props.invoice}
            reloadHandler={props.reloadHandler}
            markAsDoneHandler={props.markAsDoneHandler}
            setLoading={props.setLoading}
          />
        ) : undefined
      }
      columnsLeft={props.columnsLeft}
      onBackButtonClick={props.isFinalPreview ? onShowReview : undefined}
      disableSuccessMessages={props.disableSuccessMessages}
    />
  );
};

export default InvoiceFooter;
